import { useCallback, useRef, useEffect } from 'react';

/**
 * Custom hook for managing memory usage in photo galleries
 * Handles cleanup of off-screen images and memory leak prevention
 * @param {Object} options
 * @param {number} options.bufferMultiplier - How many viewport heights to keep in memory (default: 2)
 * @param {number} options.cleanupInterval - Cleanup interval in ms (default: 5000)
 * @returns {Object} Memory management functions and state
 */
export const useMemoryManagement = (options = {}) => {
  const {
    bufferMultiplier = 2,
    cleanupInterval = 5000
  } = options;

  const imageRefs = useRef(new Map());
  const cleanupTimeoutRef = useRef(null);
  const lastCleanupRef = useRef(Date.now());

  /**
   * Register an image element for memory management
   * @param {string} key - Unique identifier for the image
   * @param {HTMLImageElement} element - Image element to track
   */
  const registerImage = useCallback((key, element) => {
    if (element && element instanceof HTMLImageElement) {
      imageRefs.current.set(key, {
        element,
        registeredAt: Date.now(),
        lastVisible: Date.now()
      });
    }
  }, []);

  /**
   * Unregister an image element
   * @param {string} key - Unique identifier for the image
   */
  const unregisterImage = useCallback((key) => {
    const imageData = imageRefs.current.get(key);
    if (imageData) {
      const { element } = imageData;
      // Clean up the image element
      if (element.parentNode) {
        element.parentNode.removeChild(element);
      }
      // Revoke object URL if it's a blob URL
      if (element.src && element.src.startsWith('blob:')) {
        URL.revokeObjectURL(element.src);
      }
      imageRefs.current.delete(key);
    }
  }, []);

  /**
   * Check if an element is within the viewport buffer
   * @param {HTMLElement} element - Element to check
   * @returns {boolean} Whether element is in viewport buffer
   */
  const isInViewportBuffer = useCallback((element) => {
    if (!element) return false;

    // Simplified viewport detection for nested scroll containers
    // Use getBoundingClientRect which works regardless of nesting
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Check if element is within a generous viewport buffer
    // This works for both window scrolling and nested scroll containers
    const bufferTop = -viewportHeight * bufferMultiplier; // Allow elements above viewport
    const bufferBottom = viewportHeight * (1 + bufferMultiplier); // Allow elements below viewport
    const bufferLeft = -viewportWidth * bufferMultiplier; // Allow elements left of viewport
    const bufferRight = viewportWidth * (1 + bufferMultiplier); // Allow elements right of viewport

    return rect.bottom >= bufferTop &&
           rect.top <= bufferBottom &&
           rect.right >= bufferLeft &&
           rect.left <= bufferRight;
  }, [bufferMultiplier]);

  /**
   * Update visibility timestamp for an image
   * @param {string} key - Unique identifier for the image
   */
  const updateVisibility = useCallback((key) => {
    const imageData = imageRefs.current.get(key);
    if (imageData) {
      imageData.lastVisible = Date.now();
    }
  }, []);

  /**
   * Update visibility for all currently visible images
   */
  const updateVisibleImages = useCallback(() => {
    const now = Date.now();
    imageRefs.current.forEach((imageData, key) => {
      const { element } = imageData;
      if (element && isInViewportBuffer(element)) {
        imageData.lastVisible = now;
      }
    });
  }, [isInViewportBuffer]);

  /**
   * Clean up off-screen images that are no longer needed
   */
  const cleanupOffscreenImages = useCallback(() => {
    const now = Date.now();
    const minAge = 120000; // Keep images for at least 2 minutes (very conservative)
    const maxAge = 1800000; // Force cleanup after 30 minutes (very long)

    let cleanupCount = 0;

    imageRefs.current.forEach((imageData, key) => {
      const { element, lastVisible, registeredAt } = imageData;
      const age = now - registeredAt;
      const timeSinceVisible = now - lastVisible;

      // Don't cleanup recently registered or recently visible images
      if (age < minAge || timeSinceVisible < minAge) {
        return;
      }

      // Only cleanup if element is no longer in DOM
      if (!element.parentNode) {
        console.log(`Cleaning up image ${key} - no longer in DOM`);
        unregisterImage(key);
        cleanupCount++;
      }
      // Very conservative viewport-based cleanup for extremely old images
      // Only cleanup if image is very old AND definitely out of viewport
      else if (age > maxAge && !isInViewportBuffer(element)) {
        // Double-check that the element is actually far from viewport
        const rect = element.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        // Only cleanup if element is at least 2 viewport heights away
        const farFromViewport = rect.bottom < -viewportHeight * 2 ||
                               rect.top > viewportHeight * 2 ||
                               rect.right < -viewportWidth * 2 ||
                               rect.left > viewportWidth * 2;

        if (farFromViewport) {
          console.log(`Cleaning up very old image ${key} - age: ${age}ms, far from viewport`);
          unregisterImage(key);
          cleanupCount++;
        }
      }
    });

    if (cleanupCount > 0) {
      console.log(`Memory cleanup: removed ${cleanupCount} images`);
    }

    lastCleanupRef.current = now;
  }, [isInViewportBuffer, unregisterImage]);

  /**
   * Force cleanup of all images
   */
  const forceCleanup = useCallback(() => {
    imageRefs.current.forEach((_, key) => {
      unregisterImage(key);
    });
  }, [unregisterImage]);

  /**
   * Get memory usage statistics
   * @returns {Object} Memory statistics
   */
  const getMemoryStats = useCallback(() => {
    return {
      registeredImages: imageRefs.current.size,
      lastCleanup: lastCleanupRef.current,
      timeSinceCleanup: Date.now() - lastCleanupRef.current
    };
  }, []);

  /**
   * Get registered images count
   * @returns {number} Number of registered images
   */
  const getRegisteredImagesCount = useCallback(() => {
    return imageRefs.current.size;
  }, []);

  /**
   * Start periodic cleanup
   */
  const startPeriodicCleanup = useCallback(() => {
    const performCleanup = () => {
      cleanupOffscreenImages();
      cleanupTimeoutRef.current = setTimeout(performCleanup, cleanupInterval);
    };

    performCleanup();
  }, [cleanupOffscreenImages, cleanupInterval]);

  /**
   * Stop periodic cleanup
   */
  const stopPeriodicCleanup = useCallback(() => {
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }
  }, []);

  // Start periodic cleanup on mount
  useEffect(() => {
    startPeriodicCleanup();
    return () => {
      stopPeriodicCleanup();
      forceCleanup();
    };
  }, [startPeriodicCleanup, stopPeriodicCleanup, forceCleanup]);

  // Cleanup on page visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden, aggressive cleanup
        forceCleanup();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [forceCleanup]);

  // Cleanup on memory pressure (if supported)
  useEffect(() => {
    const handleMemoryPressure = () => {
      console.warn('Memory pressure detected, performing cleanup');
      forceCleanup();
    };

    if ('memory' in performance) {
      // Chrome/Edge memory pressure event
      window.addEventListener('memorypressure', handleMemoryPressure);
      return () => window.removeEventListener('memorypressure', handleMemoryPressure);
    }
  }, [forceCleanup]);

  return {
    registerImage,
    unregisterImage,
    updateVisibility,
    updateVisibleImages,
    cleanupOffscreenImages,
    forceCleanup,
    getMemoryStats,
    getRegisteredImagesCount,
    startPeriodicCleanup,
    stopPeriodicCleanup
  };
};
